/* eslint-disable react-hooks/rules-of-hooks */
import { useEffect } from 'react';

import { useRumAction } from './index.js';

const startSuffix = '-start';

const buildUniqueMetricName = (
  metricName: string,
  uniqueIdentifier?: string,
): string =>
  uniqueIdentifier ? `${metricName}-${uniqueIdentifier}` : metricName;

/**
 * Starts a performance measurement by placing a `performance.mark`.
 * Must be paired with {@link useStopMeasureWhenRendered} to complete the measurement.
 *
 * @param metricName - The name of the metric to measure.
 * @param options - Optional configuration.
 * @param options.uniqueIdentifier - An optional identifier to distinguish between
 *   multiple concurrent measurements of the same metric (e.g. a row ID or entity ID).
 *
 *   **Important:** If you provide a `uniqueIdentifier` here, you **must** pass the same
 *   `uniqueIdentifier` to {@link useStopMeasureWhenRendered}. Otherwise the stop hook
 *   will not find the matching start mark and the measurement will be silently dropped.
 *
 *   **Note:** The `uniqueIdentifier` is used only for internal performance tracking to
 *   allow multiple concurrent measurements. It is **not** included in the metric name sent
 *   to Datadog. All measurements will be reported under the same `metricName`, regardless
 *   of their `uniqueIdentifier`.
 *
 * @example
 * ```ts
 * // Without uniqueIdentifier
 * startMeasure('widget-load');
 *
 * // With uniqueIdentifier — the same value must be passed to useStopMeasureWhenRendered
 * startMeasure('widget-load', { uniqueIdentifier: widgetId });
 * ```
 */
export function startMeasure(
  metricName: string,
  options?: { uniqueIdentifier?: string },
) {
  if (import.meta.env.VITEST) {
    return;
  }

  const metricNameUnique = buildUniqueMetricName(
    metricName,
    options?.uniqueIdentifier,
  );

  performance.mark(metricNameUnique + startSuffix);
}

/**
 * React hook that completes a performance measurement previously started with
 * {@link startMeasure}. It records the duration on the first render after mount
 * and reports it to Datadog RUM as a custom action.
 *
 * @param metricName - Must match the `metricName` passed to {@link startMeasure}.
 * @param metadata - Contextual data sent alongside the metric to Datadog.
 * @param metadata.react - React-specific metadata.
 * @param metadata.react.component - The name of the component being measured.
 * @param options - Optional configuration.
 * @param options.shouldIgnoreRender - When `true`, skips the measurement on this render.
 *   Useful for conditional loading states where the meaningful render hasn't happened yet.
 * @param options.uniqueIdentifier - An optional identifier to match the corresponding
 *   {@link startMeasure} call. Used for internal tracking only to support concurrent measurements;
 *   not sent to Datadog.
 *
 *   **Important:** If a `uniqueIdentifier` was provided to {@link startMeasure}, the
 *   **same value** must be provided here. A mismatch (or omission) will cause the hook
 *   to fail silently because the underlying `performance.mark` won't be found.
 *
 *   **Note:** The `uniqueIdentifier` is used only for internal performance tracking to
 *   allow multiple concurrent measurements. It is **not** included in the metric name sent
 *   to Datadog. All measurements will be reported under the same `metricName`, regardless
 *   of their `uniqueIdentifier`.
 *
 * @example
 * ```tsx
 * // Start the measure before navigation / data fetch
 * startMeasure('widget-load', { uniqueIdentifier: widgetId });
 *
 * // Inside the target component
 * useStopMeasureWhenRendered(
 *   'widget-load',
 *   { react: { component: 'WidgetPanel' } },
 *   { uniqueIdentifier: widgetId },
 * );
 * ```
 */
export function useStopMeasureWhenRendered(
  metricName: string,
  metadata: {
    react: {
      [key: string]: string;
      component: string;
    };
    [key: string]: any;
  },
  options?: { shouldIgnoreRender?: boolean; uniqueIdentifier?: string },
) {
  if (import.meta.env.VITEST) {
    return;
  }
  const report = useRumAction('measure');

  const metricNameUnique = buildUniqueMetricName(
    metricName,
    options?.uniqueIdentifier,
  );
  const metricNameStart = metricNameUnique + startSuffix;

  useEffect(() => {
    if (options?.shouldIgnoreRender || import.meta.env.VITEST) {
      return;
    }

    const markEntries = performance.getEntriesByName(metricNameStart);
    const mark = markEntries.find((e) => e instanceof PerformanceMark);

    if (!mark) {
      return;
    }

    performance.measure(metricNameUnique, metricNameStart);
    const measureEntries = performance.getEntriesByName(metricNameUnique);
    const measure = measureEntries.find((e) => e instanceof PerformanceMeasure);

    if (!measure) {
      return;
    }

    report(metricName, {
      ...metadata,
      metricName,
      duration: measure.duration,
    });

    performance.clearMarks(metricNameStart);
    performance.clearMeasures(metricNameUnique);
  }, [metricName, metadata, metricNameUnique, metricNameStart]);
}
